import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarPlus, Building2, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { http, formatApiError } from "../lib/api";
import { useActivities } from "../lib/activities";
import { NewActivityButton } from "./NewActivity";
import { ClientModal } from "../pages/Clients";
import { SiteModal } from "../pages/Sites";

const frd = (d) => (d ? d.split("-").reverse().join("/") : "");
const inp = "w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white";

function MissingSite({ pv, sites, siteId, setSiteId, onCreateClient, onCreateSite }) {
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="prestation-missing-site">
      <div className="text-sm font-semibold text-amber-900">Aucun site enregistré pour cette prestation</div>
      {sites.length > 0 && (
        <select data-testid="prestation-pick-site" className={inp} value={siteId} onChange={(e) => setSiteId(e.target.value)}>
          <option value="">— Choisir un site existant —</option>
          {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      )}
      <div className="flex flex-wrap gap-2">
        {!pv.client && <button type="button" data-testid="prestation-create-client" onClick={onCreateClient} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 text-white text-xs font-semibold"><Building2 className="h-3.5 w-3.5" /> 1. Créer le client{pv.client_name ? ` « ${pv.client_name} »` : ""}</button>}
        <button type="button" data-testid="prestation-create-site" disabled={!pv.client} onClick={onCreateSite} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-sky-600 text-white text-xs font-semibold disabled:opacity-40"><MapPin className="h-3.5 w-3.5" /> {pv.client ? "" : "2. "}Créer le site{pv.client ? ` pour ${pv.client.name}` : ""}</button>
      </div>
    </div>
  );
}

export const PrestationPlanningDialog = ({ prestation, onClose, onDone }) => {
  const acts = useActivities();
  const nav = useNavigate();
  const [pv, setPv] = useState(null);
  const [siteId, setSiteId] = useState("");
  const [sites, setSites] = useState([]);
  const [clients, setClients] = useState([]);
  const [activity, setActivity] = useState("");
  const [modal, setModal] = useState(null);
  const [newClient, setNewClient] = useState(null);
  const [busy, setBusy] = useState(false);
  const preview = (sid) => http.post(`/prestations/${prestation.id}/planning-preview`, { site_id: sid || null })
    .then((r) => setPv(r.data)).catch((e) => { toast.error(formatApiError(e)); onClose(); });
  const loadRefs = () => Promise.all([http.get("/sites"), http.get("/clients")]).then(([s, c]) => { setSites(s.data); setClients(c.data); });
  useEffect(() => { preview(""); loadRefs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const pickSite = (sid) => { setSiteId(sid); preview(sid); };
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await http.post(`/prestations/${prestation.id}/planning`, { site_id: pv.site?.id, activity: activity || null });
      toast.success(`${r.data.created} vacation(s) à pourvoir créées — les responsables planning sont prévenus`);
      onDone?.(); onClose(); nav(`/planning?date=${r.data.first}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Dialog open={!modal} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-lg" data-testid="prestation-planning-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl flex items-center gap-2"><CalendarPlus className="h-5 w-5 text-sky-600" /> Créer le planning</DialogTitle></DialogHeader>
          {!pv ? <div className="py-8 text-center text-sm text-slate-500">Chargement…</div> : (
            <div className="space-y-3 text-sm">
              <div className="text-slate-600">« {prestation.name} » · devis <b>{pv.devis?.number}</b> accepté</div>
              <div className="grid grid-cols-3 gap-2" data-testid="prestation-planning-summary">
                <div className="rounded-md bg-slate-50 p-2"><div className="text-[10px] uppercase tracking-widest text-slate-500">Vacations</div><div className="text-xl font-bold" data-testid="prestation-planning-count">{pv.vacations}</div></div>
                <div className="rounded-md bg-slate-50 p-2"><div className="text-[10px] uppercase tracking-widest text-slate-500">Période</div><div className="font-semibold">{frd(pv.first)} → {frd(pv.last)}</div></div>
                <div className="rounded-md bg-slate-50 p-2"><div className="text-[10px] uppercase tracking-widest text-slate-500">Horaires</div><div className="font-semibold">{pv.start_time} – {pv.end_time}</div></div>
              </div>
              <div className="text-xs text-slate-500">{pv.days} jour(s) × {pv.agents_count} agent(s) · vacations créées sans agent, à pourvoir dans le planning.</div>
              {pv.site ? <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-900" data-testid="prestation-planning-site">Site : <b>{pv.site.name}</b>{pv.client ? ` · client ${pv.client.name}` : ""}</div>
                : <MissingSite pv={pv} sites={sites} siteId={siteId} setSiteId={pickSite} onCreateClient={() => setModal("client")} onCreateSite={() => setModal("site")} />}
              <label className="block text-xs text-slate-500">Activité des vacations
                <select data-testid="prestation-planning-activity" className={inp} value={activity} onChange={(e) => setActivity(e.target.value)}>
                  <option value="">— Non précisé —</option>
                  {acts.map((a) => <option key={a.code} value={a.code}>{a.label}</option>)}
                </select>
                <NewActivityButton testid="prestation-new-activity" className="mt-1" onCreated={setActivity} />
              </label>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-slate-300" data-testid="prestation-planning-cancel">Annuler</button>
            <button type="button" data-testid="prestation-planning-confirm" disabled={!pv?.site || busy} onClick={confirm} className="px-4 py-2 text-sm rounded-md bg-sky-600 text-white font-semibold disabled:opacity-40">{busy ? "Création…" : `Créer ${pv?.vacations || ""} vacation(s)`}</button>
          </div>
        </DialogContent>
      </Dialog>
      {modal === "client" && <ClientModal value={{ name: pv.client_name }} onClose={() => setModal(null)} onSaved={(c) => { setNewClient(c); loadRefs(); preview(siteId); setTimeout(() => setModal("site"), 300); }} />}
      {modal === "site" && <SiteModal value={{ name: prestation.name, client_id: pv.client?.id || newClient?.id || null }} clients={clients} onClose={() => setModal(null)}
        onSaved={(_c, s) => { loadRefs(); if (s?.id) pickSite(s.id); }} />}
    </>
  );
};
